import { AppShell, Button, cn, RoleChip } from "@jod/ui";
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { LayoutDashboard, ScrollText, Store, UserCog } from "lucide-react";
import type { ReactNode } from "react";
import { getPlatformPrincipal, signOutPlatform } from "../lib/auth-session.ts";

export const Route = createFileRoute("/_app")({
  beforeLoad: () => {
    if (!getPlatformPrincipal()) throw redirect({ to: "/login" });
  },
  component: AppLayout,
});

type NavPath = "/dashboard" | "/venues" | "/operators" | "/audit";

const NAV: {
  key: string;
  label: string;
  to: NavPath;
  icon: ReactNode;
  superAdminOnly?: boolean;
}[] = [
  { key: "dashboard", label: "Dashboard", to: "/dashboard", icon: <LayoutDashboard /> },
  { key: "venues", label: "Venues", to: "/venues", icon: <Store /> },
  {
    key: "operators",
    label: "Operators",
    to: "/operators",
    icon: <UserCog />,
    superAdminOnly: true,
  },
  { key: "audit", label: "Audit Log", to: "/audit", icon: <ScrollText /> },
];

function AppLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const principal = getPlatformPrincipal();
  const visibleNav = NAV.filter(
    (item) => !item.superAdminOnly || principal?.role === "super_admin",
  );

  const sidebar = (
    <>
      <div className="px-2 pb-4">
        <p className="font-display font-semibold text-lg leading-tight">Just One Drink</p>
        <p className="text-muted-foreground text-xs">Platform Backoffice · Internal</p>
      </div>
      <nav className="flex flex-col gap-1">
        {visibleNav.map((item) => (
          <button
            type="button"
            key={item.key}
            onClick={() => navigate({ to: item.to })}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm [&_svg]:size-4",
              pathname.startsWith(item.to)
                ? "bg-primary/10 font-medium text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </nav>
    </>
  );

  const topbar = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <div>
        <span className="font-display font-medium text-sm">{principal?.email}</span>
        <p className="text-muted-foreground text-xs">All access is audited (FR-SADMIN-080)</p>
      </div>
      <div className="flex items-center gap-2">
        {principal ? <RoleChip label={principal.role} /> : null}
        <Button
          variant="ghost"
          size="sm"
          className="text-foreground"
          onClick={() => {
            signOutPlatform();
            void navigate({ to: "/login" });
          }}
        >
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <AppShell sidebar={sidebar} topbar={topbar}>
        <Outlet />
      </AppShell>
    </div>
  );
}
